import React from "react";
import { Download } from "lucide-react";
import { ReactComponent as NoInvoicesMark } from "../../../assets/svg/billing/no billing history icon.svg";
import { EmptyState } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import { planLine } from "../invoiceLine";
import { formatMoney } from "../pricing";
import InvoiceStatusBadge from "./InvoiceStatusBadge";
import type { SubscriptionInvoice } from "../types/subscriptions.types";

interface InvoiceCardsProps {
  invoices: SubscriptionInvoice[];
  /** Fetches this invoice's PDF and hands it to the browser. */
  onDownload: (invoice: SubscriptionInvoice) => void;
  /** The invoice whose PDF is being fetched, so one card spins and the rest
   *  stay pressable. */
  downloading?: string | null;
  /** Whether anything has ever been bought — see `BillingHistoryCard`, which
   *  is where the two empty states are explained. */
  purchased?: boolean;
}

/**
 * The billing history as it reads on a phone: one card per invoice instead of
 * a table.
 *
 * The table needs 620px for its four columns and the download after them,
 * which is more than a phone has — so on one it either clips the date off the
 * right edge or asks the reader to scroll sideways inside a card that is
 * itself on a page. The row becomes a card on the page's own background
 * instead, carrying the same things in the order they are wanted: which
 * invoice and where its term stands, what was bought and for how long, the day
 * it was taken, and what it came to.
 *
 * Laid out the way `ServiceCards` lays a service out — a title over a rule,
 * the number that matters under it — because the two are the same kind of
 * thing to a reader: a list of records read on the same phone.
 *
 * The card is not itself pressable. An invoice is a document rather than a
 * screen: there is nothing behind it to open, and the one thing that can be
 * done with it is the download, which is a button on the card rather than a
 * sheet reached through it.
 *
 * Which of the two renders is `BillingHistoryCard`'s decision — see
 * `useIsMobile`.
 */
const InvoiceCards: React.FC<InvoiceCardsProps> = ({
  invoices,
  onDownload,
  downloading = null,
  purchased = true,
}) => {
  if (invoices.length === 0) {
    return purchased ? (
      // Not an empty state with a picture: a plan bought minutes ago has no
      // invoice until the first charge settles, and this is that gap said in
      // one line.
      <p className="card-surface border border-line bg-white px-4 py-8 text-center text-[14px] text-ink-muted">
        Your invoices will appear here once your first payment has been
        collected.
      </p>
    ) : (
      // A trial has never been charged for anything, so this is the first-run
      // state and not a list reporting a result — see `purchased`.
      <div className="card-surface border border-line bg-white px-4 py-6">
        <EmptyState
          variant="welcome"
          size="sm"
          illustration={<NoInvoicesMark aria-hidden className="h-14 w-14" />}
          title="No billing history yet"
          // One paragraph rather than the table's two lines: the break there
          // is set to the width of a column head, and a phone sets its own.
          description="You haven't purchased a paid subscription yet. Once you do, your invoices and payment history will appear here."
        />
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {invoices.map((invoice) => (
        <li
          key={invoice.id}
          // The same corner and hairline the cards above it wear — two radii a
          // thumb's width apart read as two different surfaces.
          className="card-surface border border-line bg-white p-4"
        >
          {/* `min-w-0` is what lets a long invoice number shorten rather than
              stretch the card. */}
          <div className="min-w-0">
            {/* The badge rides on the number's line rather than below it: a
                phone's card has one thing at the top and this is a fact about
                that thing, not a second row. It keeps its width and the number
                gives way — see the table, which does the same. */}
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-[15px] font-medium text-ink">
                {invoice.number}
              </p>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
            <p className="mt-1 truncate text-[13px] text-ink">
              {planLine(invoice)}
            </p>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              {formatCreatedOn(new Date(invoice.issuedOn))}
            </p>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
            <span className="text-[16px] font-semibold tabular-nums text-ink">
              {formatMoney(invoice.amount)}
            </span>
            {/* Named as well as drawn, unlike the table's icon-only button: a
                card has the room for the word, and a lone glyph at the end of
                a row is a thing to guess at on a screen with no tooltip. The
                full name still says which invoice it fetches, since every card
                carries the same word. */}
            <button
              type="button"
              onClick={() => onDownload(invoice)}
              disabled={downloading === invoice.id}
              aria-label={`Download invoice ${invoice.number}`}
              className="-mr-1.5 flex items-center gap-1.5 rounded-md px-1.5 py-1.5 text-[13px] font-medium text-brand transition-colors hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              {downloading === invoice.id ? (
                <span
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                />
              ) : (
                <Download aria-hidden="true" className="h-4 w-4" />
              )}
              Download
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default InvoiceCards;
